import { createRouter, createWebHistory, type RouteLocationNormalized, type RouteRecordRaw } from 'vue-router';
import { usePB } from '@/composables/database/use-pocket-base.ts';
import { loggedInGuard } from '@/router/guards/logged-in-guard.ts';
import { isRouteLoading } from '@/utils/is-route-loading.ts';
import { popupStack } from '@/utils/popups.ts';
import { moderatorGuard } from '@/router/guards/moderator-guard.ts';
import { adminGuard } from '@/router/guards/admin-guard.ts';
import { initializeFeatureFlags, isFeatureEnabled } from '@/composables/use-feature-flags.ts';
import type { FeatureFlagKey } from '@/types/feature-flags.ts';
import { useAccountCapabilities } from '@/composables/use-account-capabilities.ts';
import { isPersonalSettingsSection } from '@/components/settings/settings-navigation.ts';
import type { ModalTitleKey, ModalVariant } from '@/types/modal.ts';
import { createBusinessSettingsScrollMemory } from '@/router/business-settings-scroll.ts';
import { primaryPageScrollMemory } from '@/router/primary-page-scroll.ts';
import { defaultScrollMemory, resolveDefaultScroll } from '@/router/router-scroll.ts';

declare module 'vue-router' {
  interface RouteMeta {
    featureFlag?: FeatureFlagKey;
    pageStateKey?: 'events' | 'groups';
  }
}

function modalProps(variant: ModalVariant, titleKey: ModalTitleKey, returnUrl: string) {
  return { variant, titleKey, cancelable: true, swipeToClose: true, returnUrl } as const;
}

function businessPostCommentsRoute(): RouteRecordRaw {
  return {
    path: 'post-comments/:postId',
    component: () => import('../layouts/ModalLayout.vue'),
    props: modalProps('detail', 'modal.comments', '../../'),
    children: [{ path: '', component: () => import('../views/businesses/BusinessPostCommentsView.vue') }],
  };
}

function groupViewRoute(path = ':groupId', returnUrl = '../'): RouteRecordRaw {
  return {
    path,
    component: () => import('../layouts/ModalLayout.vue'),
    props: modalProps('detail', 'modal.groupDetails', returnUrl),
    children: [
      {
        path: '',
        component: () => import('../views/groups/GroupView.vue'),
        children: [
          {
            path: 'post-comments',
            component: () => import('../layouts/ModalLayout.vue'),
            props: modalProps('detail', 'modal.comments', '../../'),
            children: [
              {
                path: ':postId',
                component: () => import('../views/groups/GroupPostCommentsView.vue'),
                children: [
                  {
                    path: 'profile/:userId',
                    component: () => import('../components/UserProfileModal.vue'),
                  },
                ],
              },
            ],
          },
          {
            path: 'members',
            component: () => import('../layouts/ModalLayout.vue'),
            props: modalProps('detail', 'modal.members', '../'),
            children: [
              {
                path: '',
                component: () => import('../components/groups/GroupMembersList.vue'),
                children: [
                  {
                    path: ':userId',
                    component: () => import('../components/UserProfileModal.vue'),
                  },
                ],
              },
            ],
          },
          {
            path: 'events/:id',
            component: () => import('../layouts/ModalLayout.vue'),
            props: modalProps('detail', 'modal.eventDetails', '../../'),
            children: [
              {
                path: '',
                component: () => import('../views/events/EventView.vue'),
                props: (route) => ({ eventId: route.params.id }),
                children: [eventCommentsRoute, eventAttendeesRoute, subEventViewRoute],
              },
            ],
          },
          {
            path: 'create-event',
            component: () => import('../layouts/ModalLayout.vue'),
            beforeEnter: [loggedInGuard],
            props: modalProps('form', 'modal.createEvent', '../'),
            children: [{ path: '', component: () => import('../views/events/CreateEventView.vue') }],
          },
          {
            path: 'edit',
            component: () => import('../layouts/ModalLayout.vue'),
            props: modalProps('form', 'modal.editGroup', '../'),
            children: [{ path: '', component: () => import('../views/groups/EditGroupView.vue') }],
          },
        ],
      },
    ],
  };
}

const groupBusinessViewRoute: RouteRecordRaw = {
  path: 'businesses/:businessId',
  component: () => import('../layouts/ModalLayout.vue'),
  props: modalProps('detail', 'modal.businessDetails', '../../'),
  meta: { scrollToTop: true, featureFlag: 'business_community' },
  children: [
    {
      path: '',
      component: () => import('../views/businesses/PublicBusinessCommunityView.vue'),
      children: [
        businessPostCommentsRoute(),
        {
          path: 'book',
          component: () => import('../layouts/ModalLayout.vue'),
          props: modalProps('form', 'modal.resourceBooking', '../?tab=booking'),
          meta: { featureFlag: 'resource_booking' },
          children: [{ path: '', component: () => import('../views/resources/ResourceBookingView.vue') }],
        },
      ],
    },
  ],
};

const eventCommentsRoute: RouteRecordRaw = {
  path: 'post-comments/:postId',
  component: () => import('../layouts/ModalLayout.vue'),
  props: modalProps('detail', 'modal.comments', '../../'),
  children: [{ path: '', component: () => import('../views/events/EventPostCommentsView.vue') }],
};

const eventAttendeesRoute: RouteRecordRaw = {
  path: 'attendees',
  component: () => import('../layouts/ModalLayout.vue'),
  props: modalProps('detail', 'modal.attendees', '../'),
  children: [
    {
      path: '',
      component: () => import('../components/events/EventAttendeesList.vue'),
      children: [{ path: ':userId', component: () => import('../components/UserProfileModal.vue') }],
    },
  ],
};

const subEventViewRoute: RouteRecordRaw = {
  path: 'sessions/:sessionId',
  component: () => import('../layouts/ModalLayout.vue'),
  props: modalProps('detail', 'modal.subEventDetails', '../../'),
  children: [
    {
      path: '',
      component: () => import('../views/events/EventView.vue'),
      props: (route) => ({ eventId: route.params.sessionId }),
      children: [
        eventCommentsRoute,
        eventAttendeesRoute,
        {
          path: 'edit',
          component: () => import('../layouts/ModalLayout.vue'),
          beforeEnter: [loggedInGuard],
          props: modalProps('form', 'modal.editEvent', '../'),
          children: [{ path: '', component: () => import('../views/events/CreateEventView.vue') }],
        },
      ],
    },
  ],
};

function directBusinessPersonalRouteGuard(to: { path: string }) {
  if (useAccountCapabilities().canUsePersonalActivity.value) return;
  if (to.path.startsWith('/settings/admin') || to.path.startsWith('/settings/moderation')) return;
  if (to.path === '/settings') return { path: '/settings/business/overview', replace: true };
  return { path: '/settings/business/profile', replace: true };
}

function legacySettingsSectionGuard(to: RouteLocationNormalized) {
  if (to.path !== '/settings' || !isPersonalSettingsSection(to.query.section)) return;
  const { section, ...query } = to.query;
  return { path: `/settings/${section}`, query, hash: to.hash, replace: true };
}

const eventViewRoute: RouteRecordRaw = {
  path: ':id',
  component: () => import('../layouts/ModalLayout.vue'),
  props: modalProps('detail', 'modal.eventDetails', '../'),
  children: [
    {
      path: '',
      component: () => import('../views/events/EventView.vue'),
      props: (route) => ({ eventId: route.params.id }),
      children: [
        eventCommentsRoute,
        eventAttendeesRoute,
        subEventViewRoute,
        {
          path: 'edit',
          component: () => import('../layouts/ModalLayout.vue'),
          beforeEnter: [loggedInGuard],
          props: modalProps('form', 'modal.editEvent', '../'),
          children: [{ path: '', component: () => import('../views/events/CreateEventView.vue') }],
        },
      ],
    },
  ],
};

const businessSettingsScrollMemory = createBusinessSettingsScrollMemory();

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    return (
      businessSettingsScrollMemory.resolve(to, savedPosition) ??
      primaryPageScrollMemory.resolve(to, from, savedPosition) ??
      resolveDefaultScroll(to, savedPosition, defaultScrollMemory.position(to))
    );
  },
  routes: [
    {
      path: '/feature-unavailable',
      component: () => import('../views/FeatureUnavailableView.vue'),
      meta: { scrollToTop: true },
    },
    {
      path: '/embed/businesses/:businessId/booking',
      component: () => import('../views/businesses/BusinessBookingEmbedView.vue'),
      meta: { scrollToTop: true, embedded: true, featureFlag: 'booking_pages_embeds' },
    },
    {
      path: '/auth',
      beforeEnter: () => {
        const pb = usePB();
        if (pb.isLoggedIn.value) {
          return { path: '/' };
        }
      },
      component: () => import('../layouts/AuthLayout.vue'),
      children: [
        {
          path: '',
          component: () => import('../views/AuthView.vue'),
        },
        {
          path: 'email',
          component: () => import('../views/auth/EmailAuthView.vue'),
        },
        {
          path: 'confirm',
          component: () => import('../views/auth/ConfirmOTPView.vue'),
        },
      ],
    },
    {
      path: '/',
      sensitive: true,
      redirect: '/events',
      component: () => import('../layouts/DefaultLayout.vue'),
      children: [
        {
          path: '/events/:eventId/company',
          component: () => import('../layouts/ModalLayout.vue'),
          beforeEnter: [loggedInGuard, directBusinessPersonalRouteGuard],
          props: modalProps('detail', 'modal.companyChat', '/events'),
          meta: { featureFlag: 'open_to_company' },
          children: [{ path: '', component: () => import('../views/events/EventCompanyRoomView.vue') }],
        },
        {
          path: '/onboarding',
          component: () => import('../layouts/ModalLayout.vue'),
          beforeEnter: [loggedInGuard, directBusinessPersonalRouteGuard],
          props: modalProps('form', 'modal.onboarding', '/settings'),
          children: [{ path: '', component: () => import('../views/OnboardingView.vue') }],
        },
        {
          path: '/events',
          component: () => import('../views/EventsView.vue'),
          meta: { pageStateKey: 'events' },
          redirect: (to) => ({ path: '/events/explore', query: to.query }),
          children: [
            {
              path: 'explore',
              component: () => import('../views/events/ExploreEventsView.vue'),
              children: [eventViewRoute],
            },
            {
              path: 'now',
              component: () => import('../views/events/NearbyNowEventsView.vue'),
              meta: { featureFlag: 'nearby_now' },
              children: [eventViewRoute],
            },
            {
              path: 'pulse',
              component: () => import('../views/events/NeighborhoodPulseView.vue'),
              meta: { featureFlag: 'neighborhood_pulse' },
            },
            {
              path: 'plans',
              component: () => import('../views/events/EventPlansView.vue'),
              meta: { featureFlag: 'collaborative_plans' },
              beforeEnter: [loggedInGuard, directBusinessPersonalRouteGuard],
              children: [
                {
                  path: 'create',
                  component: () => import('../layouts/ModalLayout.vue'),
                  props: modalProps('form', 'modal.createPlan', '../'),
                  children: [
                    {
                      path: '',
                      component: () => import('../views/events/CreateEventPlanView.vue'),
                      children: [
                        {
                          path: 'events/:eventId',
                          component: () => import('../layouts/ModalLayout.vue'),
                          props: modalProps('detail', 'modal.eventDetails', '../../'),
                          children: [
                            {
                              path: '',
                              component: () => import('../views/events/EventView.vue'),
                              props: (route) => ({ eventId: route.params.eventId }),
                              children: [eventCommentsRoute, eventAttendeesRoute, subEventViewRoute],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
                {
                  path: ':planId',
                  component: () => import('../layouts/ModalLayout.vue'),
                  props: modalProps('detail', 'modal.planDetails', '../'),
                  children: [{ path: '', component: () => import('../views/events/EventPlanView.vue') }],
                },
              ],
            },
            {
              path: 'my-events',
              component: () => import('../views/events/MyEventsView.vue'),
              beforeEnter: [loggedInGuard],
              children: [
                eventViewRoute,
                {
                  path: 'create',
                  component: () => import('../layouts/ModalLayout.vue'),
                  props: modalProps('form', 'modal.createEvent', '../'),
                  children: [
                    {
                      path: '',
                      component: () => import('../views/events/CreateEventView.vue'),
                    },
                  ],
                },
              ],
            },
            {
              path: 'updates',
              component: () => import('../views/events/EventUpdatesView.vue'),
              beforeEnter: [loggedInGuard],
              children: [eventViewRoute],
            },
            {
              path: 'businesses',
              redirect: (to) => ({ path: '/events/explore', query: { ...to.query, venues: 'true' } }),
            },
            {
              path: 'venues',
              redirect: (to) => ({ path: '/events/businesses', query: to.query }),
            },
          ],
        },
        {
          path: '/events/create',
          redirect: (to) => ({ path: '/events/my-events/create', query: to.query }),
          beforeEnter: [loggedInGuard],
        },
        {
          path: '/organizer/start',
          component: () => import('../layouts/ModalLayout.vue'),
          beforeEnter: [loggedInGuard],
          props: modalProps('form', 'modal.organizerSetup', '/events/my-events'),
          children: [{ path: '', component: () => import('../views/OrganizerStartView.vue') }],
        },
        {
          path: '/events/:id',
          component: () => import('../layouts/ModalLayout.vue'),
          props: modalProps('detail', 'modal.eventDetails', '/events'),
          children: [
            {
              path: '',
              component: () => import('../views/events/EventView.vue'),
              props: (route) => ({ eventId: route.params.id }),
              children: [
                eventCommentsRoute,
                eventAttendeesRoute,
                subEventViewRoute,
                {
                  path: 'edit',
                  component: () => import('../layouts/ModalLayout.vue'),
                  props: modalProps('form', 'modal.editEvent', '../'),
                  children: [
                    {
                      path: '',
                      component: () => import('../views/events/CreateEventView.vue'),
                      beforeEnter: [loggedInGuard],
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: '/my-feed',
          component: () => import('../views/MyFeedView.vue'),
          beforeEnter: [loggedInGuard, directBusinessPersonalRouteGuard],
        },
        {
          path: '/resources',
          component: () => import('../layouts/ModalLayout.vue'),
          props: modalProps('form', 'modal.resourceBooking', '/events'),
          meta: { featureFlag: 'resource_booking' },
          children: [{ path: '', component: () => import('../views/resources/ResourceBookingView.vue') }],
        },
        {
          path: '/orders',
          component: () => import('../layouts/ModalLayout.vue'),
          beforeEnter: [loggedInGuard, directBusinessPersonalRouteGuard],
          props: modalProps('detail', 'modal.orders', '/settings'),
          meta: { scrollToTop: true },
          children: [{ path: '', component: () => import('../views/CustomerOrdersView.vue') }],
        },
        {
          path: '/businesses/:businessId',
          component: () => import('../layouts/ModalLayout.vue'),
          props: modalProps('detail', 'modal.businessDetails', '/events'),
          meta: { scrollToTop: true, featureFlag: 'business_community' },
          children: [
            {
              path: '',
              component: () => import('../views/businesses/PublicBusinessCommunityView.vue'),
              children: [
                businessPostCommentsRoute(),
                {
                  path: 'book',
                  component: () => import('../layouts/ModalLayout.vue'),
                  props: modalProps('form', 'modal.resourceBooking', '../?tab=booking'),
                  meta: { featureFlag: 'resource_booking' },
                  children: [{ path: '', component: () => import('../views/resources/ResourceBookingView.vue') }],
                },
              ],
            },
          ],
        },
        {
          path: '/search',
          component: () => import('../views/SearchView.vue'),
          children: [
            {
              path: 'event/:eventId',
              component: () => import('../layouts/ModalLayout.vue'),
              props: modalProps('detail', 'modal.eventDetails', '../../'),
              children: [
                {
                  path: '',
                  component: () => import('../views/events/EventView.vue'),
                  props: (route) => ({ eventId: route.params.eventId }),
                  children: [eventCommentsRoute, eventAttendeesRoute, subEventViewRoute],
                },
              ],
            },
            {
              path: 'group/:groupId',
              component: () => import('../layouts/ModalLayout.vue'),
              props: modalProps('detail', 'modal.groupDetails', '../../'),
              children: groupViewRoute('', '../../').children,
            },
            {
              path: 'profile/:userId',
              component: () => import('../components/UserProfileModal.vue'),
            },
          ],
        },
        {
          path: '/privacy',
          component: () => import('../layouts/ModalLayout.vue'),
          props: modalProps('detail', 'modal.privacy', '/settings'),
          meta: { scrollToTop: true },
          children: [{ path: '', component: () => import('../views/PrivacyPolicyView.vue') }],
        },
        {
          path: '/groups',
          component: () => import('../views/GroupsView.vue'),
          meta: { pageStateKey: 'groups' },
          redirect: '/groups/explore',
          children: [
            {
              path: 'my-groups',
              component: () => import('../views/groups/MyGroupsView.vue'),
              beforeEnter: [loggedInGuard, directBusinessPersonalRouteGuard],
              children: [
                groupViewRoute(),
                {
                  path: 'create',
                  component: () => import('../layouts/ModalLayout.vue'),
                  props: modalProps('form', 'modal.createGroup', '../'),
                  children: [
                    {
                      path: '',
                      component: () => import('../views/groups/CreateGroupView.vue'),
                    },
                  ],
                },
              ],
            },
            {
              path: 'explore',
              component: () => import('../views/groups/ExploreGroupsView.vue'),
              children: [groupBusinessViewRoute, groupViewRoute()],
            },
            {
              path: 'updates',
              component: () => import('../views/groups/GroupUpdatesView.vue'),
              beforeEnter: [loggedInGuard, directBusinessPersonalRouteGuard],
              children: [groupViewRoute()],
            },
          ],
        },
        {
          path: '/group-invite/:token',
          component: () => import('../layouts/ModalLayout.vue'),
          beforeEnter: [directBusinessPersonalRouteGuard],
          props: modalProps('compact', 'modal.groupInvitation', '/groups'),
          meta: { featureFlag: 'group_invite_links' },
          children: [{ path: '', component: () => import('../views/groups/GroupInviteView.vue') }],
        },
        groupViewRoute('/groups/:groupId', '/groups'),
        {
          path: '/my-profile',
          component: () => import('../layouts/ModalLayout.vue'),
          beforeEnter: [loggedInGuard, directBusinessPersonalRouteGuard],
          props: modalProps('detail', 'modal.profile', '/settings'),
          children: [{ path: '', component: () => import('../views/MyProfileView.vue') }],
        },
        {
          path: '/settings/business/:businessSection(overview|operations|events|resources|community|inbox|profile|insights|team|finance|notifications|preferences|account|data)',
          component: () => import('../views/SettingsView.vue'),
          beforeEnter: [loggedInGuard],
        },
        {
          path: '/settings/:settingsSection(profile|people|availability|notifications|language|account|privacy)',
          component: () => import('../views/SettingsView.vue'),
          beforeEnter: [loggedInGuard, directBusinessPersonalRouteGuard],
        },
        {
          path: '/settings',
          component: () => import('../views/SettingsView.vue'),
          beforeEnter: [loggedInGuard, legacySettingsSectionGuard, directBusinessPersonalRouteGuard],
          children: [
            {
              path: 'moderation',
              component: () => import('../layouts/ModalLayout.vue'),
              beforeEnter: [moderatorGuard],
              props: modalProps('workspace', 'modal.moderation', '/settings'),
              children: [{ path: '', component: () => import('../views/moderation/ModerationDashboardView.vue') }],
            },
            {
              path: 'admin',
              component: () => import('../layouts/ModalLayout.vue'),
              beforeEnter: [adminGuard],
              props: modalProps('workspace', 'modal.admin', '/settings'),
              children: [
                {
                  path: '',
                  component: () => import('../views/admin/AdminDashboardView.vue'),
                  children: [
                    {
                      path: 'moderation',
                      component: () => import('../layouts/ModalLayout.vue'),
                      beforeEnter: [moderatorGuard],
                      props: modalProps('workspace', 'modal.moderation', '/settings/admin'),
                      children: [{ path: '', component: () => import('../views/moderation/ModerationDashboardView.vue') }],
                    },
                    {
                      path: 'releases/:version',
                      component: () => import('../layouts/ModalLayout.vue'),
                      beforeEnter: [adminGuard],
                      props: modalProps('detail', 'modal.releaseNotes', '/settings/admin'),
                      children: [
                        {
                          path: '',
                          component: () => import('../views/admin/ReleaseNoteView.vue'),
                          props: true,
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              path: 'business-application',
              component: () => import('../layouts/ModalLayout.vue'),
              props: modalProps('form', 'modal.businessApplication', '/settings'),
              children: [{ path: '', component: () => import('../views/venues/VenueApplicationView.vue') }],
            },
            {
              path: 'venue-application',
              redirect: '/settings/business-application',
            },
            {
              path: 'venue-dashboard',
              redirect: '/settings/business/overview',
            },
          ],
        },
        {
          path: '/settings/:invalidSettingsSection',
          redirect: (to) => ({ path: '/settings', query: to.query, hash: to.hash }),
        },
        {
          path: '/moderation',
          redirect: '/settings/moderation',
        },
        {
          path: '/admin',
          redirect: '/settings/admin',
        },
        {
          path: '/business/apply',
          redirect: '/settings/business-application',
          beforeEnter: [loggedInGuard],
        },
        {
          path: '/venue/apply',
          redirect: '/settings/business-application',
          beforeEnter: [loggedInGuard],
        },
        {
          path: '/venue/dashboard',
          redirect: '/settings/business/overview',
          beforeEnter: [loggedInGuard],
        },
      ],
    },
  ],
});

router.beforeEach((_to, from) => {
  const position = { left: window.scrollX, top: window.scrollY };
  businessSettingsScrollMemory.remember(from, position);
  primaryPageScrollMemory.remember(from, position);
});

router.beforeEach(async (to) => {
  await initializeFeatureFlags().ready;
  const key = to.meta.featureFlag;
  if (key && !isFeatureEnabled(key)) {
    return { path: '/feature-unavailable', query: { feature: key, from: to.fullPath }, replace: true };
  }
  if (to.meta.scrollToBottom) {
    window.scrollTo(0, document.body.scrollHeight);
  }
});

// Enable a loading spinner on route changes
let loadingTimeout: ReturnType<typeof setTimeout>;
router.beforeEach((to, from, next) => {
  // Start loading spinner
  // e.g., use a global state or a loading component
  clearTimeout(loadingTimeout);
  loadingTimeout = setTimeout(() => {
    isRouteLoading.value = true;
  }, 500);
  next();
});

router.afterEach(() => {
  // Stop loading spinner
  clearTimeout(loadingTimeout);
  isRouteLoading.value = false;
});

// Allow popups to block navigation
router.beforeEach((to, from, next) => {
  if (popupStack.value.some((popup) => popup.blocksNavigation)) {
    next(false);
  } else {
    next();
  }
});

export default router;
